import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

export const NewVsReturningRevenueChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const firstSeen: Record<string, string> = {};

        // First pass: determine first-seen date for each actor across all days
        for (const date of sortedDates) {
            const day = finance.dailyBreakdown![date] as Record<string, unknown>;
            const breakdown = (day.breakdown as Array<Record<string, unknown>>) || [];

            for (const item of breakdown) {
                if (item.type === 'REVENUE' && item.category === 'product_sale') {
                    const actor = item.actor as Record<string, unknown> | null;
                    const actorId = actor?.id as string;
                    if (actorId && (!firstSeen[actorId] || date < firstSeen[actorId])) {
                        firstSeen[actorId] = date;
                    }
                }
            }
        }

        // Second pass: classify revenue as new vs returning
        return sortedDates.map((date) => {
            const day = finance.dailyBreakdown![date] as Record<string, unknown>;
            const breakdown = (day.breakdown as Array<Record<string, unknown>>) || [];
            let newRevenue = 0;
            let returningRevenue = 0;

            for (const item of breakdown) {
                if (item.type === 'REVENUE' && item.category === 'product_sale') {
                    const amount = Number(item.value) || 0;
                    const actor = item.actor as Record<string, unknown> | null;
                    const actorId = actor?.id as string;

                    if (actorId && firstSeen[actorId] === date) {
                        newRevenue += amount;
                    } else {
                        returningRevenue += amount;
                    }
                }
            }

            return { date, new: newRevenue, returning: returningRevenue };
        });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name === 'new'
                                    ? t('charts.newCustomers', 'New')
                                    : t('charts.returningCustomers', 'Returning'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            wrapperStyle={{ fontSize: '10px' }}
                            formatter={(value: string) =>
                                value === 'new'
                                    ? t('charts.newCustomers', 'New')
                                    : t('charts.returningCustomers', 'Returning')
                            }
                        />
                        <Bar
                            dataKey="returning"
                            stackId="rev"
                            fill={CHART_COLORS.chart.line2}
                            radius={[0, 0, 0, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="new"
                            stackId="rev"
                            fill={CHART_COLORS.chart.line3}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
